<template>
  <environment-banner v-if="isNotProduction" :environment="environment" :branch="branch" />
  <div class="wrapper position-relative">
    <div class="enlarge-trigger position-absolute d-md-none" @click="enlargeElement" v-if="sidebarActive === true">
      <div class="bg-primary rounded-5 w-75">
        <i class="mdi mdi-chevron-double-left mdi-18px text-white enlarge-icon"></i>
      </div>
    </div>
    <navbar>
      <template v-slot:logo>
        <div class="sidebar-header d-flex align-items-center">
          <router-link
            :to="{ name: 'retailer.dashboard' }"
            class="logo text-center text-decoration-none"
            @click="handleLogoClick"
          >
            <base-logo item-class="logo-lg" alignment="horizontal" :is-tarfin-flex="Retailer.isFlex() === true" />
            <base-logo
              item-class="logo-sm"
              alignment="single"
              :height="24"
              :is-tarfin-flex="Retailer.isFlex() === true"
              :class="{ 'test-environment-style': isNotProduction }"
            />
          </router-link>
          <panel-switch />
        </div>
      </template>
      <template v-slot:sales>
        <make-sell-component />
      </template>
      <template v-slot:items>
        <list :listItems="listItemsWithBadgeCount" item-class="side-nav side-nav-light" :header="true" :user="user" />
      </template>
    </navbar>
    <div class="content-page position-relative" id="contentPage">
      <div class="content">
        <!-- Start Topbar Content-->
        <div class="navbar-custom shadow-sm px-1">
          <PanelTopbarContent />
        </div>
        <div
          class="d-flex border-bottom border-light border-opacity-50 mx--1 bg-white p-2"
          v-if="$country.is('tr') && Retailer.isAgriculture() === true && lastOrders?.length > 0 && $router.currentRoute.value.name === 'retailer.dashboard'"
        >
          <div class="font-18 fw-semibold d-flex align-items-center text-nowrap text-secondary me-2">
            <tooltip :content="$t('views.layouts.last_orders_info')" placement="top-start">
              <i class="mdi mdi-information-outline mdi-24px me-1"></i>
            </tooltip>
            {{ $t('views.layouts.last_orders') }}
          </div>
          <vue3-marquee :pause-on-hover="true" :clone="true" :duration="lastOrders.length * 3">
            <div
              v-for="order in lastOrders"
              :key="order.id"
              class="d-flex align-items-center text-nowrap text-secondary bg-marquee-item px-2 py-1 rounded ms-2"
            >
              <span class="font-16 fw-500">{{ order.product.name }}</span
              >:
              <span class="fw-semibold font-16 text-muted ms-1"
                >{{ money.ofMinor(order.cash_unit_price).toFormat('0[,]000') }} {{ currencySymbol }}/{{
                  order.product_unit.unit_name_with_rate_to_base_unit
                }}</span
              >
            </div>
          </vue3-marquee>
        </div>
        <!-- Finish Topbar Content-->
        <div class="container px-0 mx-auto position-relative">
          <base-breadcrumb v-if="!mobilePricePage" />
          <notification-bar
            v-for="notification in getSystemWideNotifications"
            :notification="notification"
            :wrapper-class="notificationWrapperClass"
            :key="notification.id"
          />
          <div class="row mx-0">
            <div class="col-12 px-0" :class="{ 'px-0': mobilePricePage }">
              <router-view></router-view>
            </div>
          </div>
        </div>
      </div>
      <footer class="footer" v-if="!mobilePricePage">
        <div class="container-fluid">
          <div class="row">
            <div class="col-md-6">
              {{
                $t(
                  Retailer.isFlex() === true ? 'views.layouts.tarfin_flex_company_mark' : 'views.layouts.tarfin_company_mark',
                  [new Date().getFullYear()]
                )
              }}
            </div>
            <div class="col-md-6">
              <div class="text-md-end footer-links d-none d-md-block">
                <a
                  v-if="Retailer.isFlex() !== true"
                  :href="$t('views.layouts.about_us_link')"
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ $t('views.layouts.about_us') }}
                </a>
                <a :href="$t('views.layouts.privacy_policy_link')" target="_blank" class="text-decoration-none">{{
                  $t('views.layouts.privacy_agreement')
                }}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <notification-permission-modal v-if="$country.is('tr') && isShowNotificationModal" />
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import _ from 'lodash'
import { Vue3Marquee } from 'vue3-marquee'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { usePageLoadingStore } from '@/stores/helpers/pageLoading.js'
import { useSideMenuStore } from '@/stores/helpers/sideMenu.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import { useDashboardStore } from '@/stores/retailer/dashboard.js'
import country from '@/utilities/country.js'
import { isAnyMobileDevice } from '@/utilities/helpers.js'
import money from '@/utilities/money.js'
import Retailer from '@/utilities/retailerHelper.js'
import sessionLockManager from '@/utilities/sessionLockManager.js'
import { NOTIFICATION_TYPES } from '@/constants/constants.js'
import { RETAILER_USER_ROLE } from '@/constants/retailerUserRole.js'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import BaseLogo from '@/components/BaseLogo.vue'
import EnvironmentBanner from '@/components/EnvironmentBanner/index.vue'
import List from '@/components/List.vue'
import Navbar from '@/components/Navbar.vue'
import NotificationBar from '@/components/NotificationBar.vue'
import NotificationPermissionModal from '@/components/NotificationPermissionModal.vue'
import MakeSellComponent from '@/components/retailer/navbar/MakeSellComponent.vue'
import PanelSwitch from '@/components/retailer/navbar/PanelSwitch.vue'
import Tooltip from '@/components/Tooltip.vue'
import PanelTopbarContent from '@/views/layouts/PanelTopbarContent.vue'

async function getUser(authStore) {
  await authStore.getUser().then(user => {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: `${user.firstname} ${user.lastname}`
    })
  })
}

export default {
  name: 'admin',
  components: {
    Tooltip,
    Vue3Marquee,
    NotificationBar,
    EnvironmentBanner,
    NotificationPermissionModal,
    Navbar,
    PanelTopbarContent,
    List,
    BaseBreadcrumb,
    BaseLogo,
    MakeSellComponent,
    PanelSwitch
  },
  data() {
    return {
      listItems: [
        {
          text: this.$t('views.layouts.summary'),
          icon: 'mdi mdi-view-dashboard',
          name: 'retailer.dashboard'
        },
        {
          text: this.$t('views.layouts.sales'),
          icon: 'mdi mdi-briefcase',
          name: 'retailer.orders.index'
        },
        {
          text: this.$t('views.layouts.tarfin_pro'),
          icon: 'mdi mdi-offer',
          name: 'retailer.tarfin-pro',
          show: () => false,
          child: [
            {
              text: this.$t('views.layouts.pro_price_list'),
              name: 'retailer.tarfin-pro'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_request_quotation'),
              name: 'retailer.tarfin-pro-requests.create'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_requests'),
              name: 'retailer.tarfin-pro-requests.index'
            }
          ]
        },
        {
          text: this.$t('views.layouts.farmers'),
          icon: 'mdi mdi-tractor',
          name: 'retailer.farmers.index',
          show: () => {
            return this.isAgricultureRetailer === true
          }
        },
        {
          text: this.$t('views.layouts.customers'),
          icon: 'mdi mdi-account-box',
          name: 'retailer.customers.index',
          show: () => {
            return (
              this.isConsumerGoodsRetailer === true || Retailer.isVehicleSales() === true
            )
          }
        },
        {
          text: this.$t('views.layouts.purchase_requests'),
          icon: 'mdi mdi-cash',
          name: 'retailer.purchase-offers.index',
          show: () => {
            return this.$country.isNot('ro') && Retailer.isAgriculture() === true
          }
        },
        {
          text: this.$t('views.layouts.price'),
          icon: 'mdi mdi-cash',
          name: 'retailer.prices.index',
          show: () => {
            return this.$country.isNot('ro') && Retailer.isAgriculture() === true
          }
        },
        {
          text: this.$t('views.layouts.current'),
          icon: 'mdi mdi-clipboard-text',
          name: 'retailer.current-account.activities.index',
          permission: 'owner',
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text: this.$t('views.layouts.sub_retailer'),
          icon: 'mdi mdi-store',
          name: 'retailer.sub-retailers.index',
          permission: 'owner',
          show: () => {
            return this.$country.isNot('ro')
          },
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text: this.$t('views.layouts.users'),
          icon: 'mdi mdi-account-multiple',
          name: 'retailer.users.index',
          permission: 'owner'
        },
        {
          text: this.$t('views.layouts.revelant_sales_team'),
          icon: 'mdi mdi-account-group',
          name: 'retailer.support.index'
        },
        {
          text: this.$t('views.layouts.brands'),
          icon: 'mdi mdi-tag-multiple',
          name: 'retailer.brands.index',
          show: () => {
            return this.$country.isNot('ro') && Retailer.isAgriculture() === true
          }
        },
        {
          text: this.$t('views.layouts.stocks'),
          icon: 'mdi mdi-view-dashboard',
          name: 'retailer.stockInputs.show',
          show: () => false
        },
        {
          text: this.$t('views.layouts.goals'),
          icon: 'mdi mdi-trophy-outline',
          name: 'retailer.goals.index',
          permission: 'owner',
          show: () => {
            return this.$country.isNot('ro')
          },
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text:
            Retailer.isVehicleSales() === true
              ? this.$t('views.layouts.car_sales_company_info')
              : this.$t('views.layouts.tarfin_company_info'),
          icon: 'mdi mdi-domain',
          name: 'retailer.tarfin.company.info'
        },
        {
          text: this.$t('views.layouts.faq_title'),
          icon: 'mdi mdi-file-document-outline',
          name: 'retailer.ro.faq.index',
          show: () => {
            return this.$country.isNot('tr')
          }
        },
        {
          text: this.$t('views.layouts.user_guide'),
          icon: 'mdi mdi-file-document-outline',
          name: 'retailer.faq.index',
          show: () => {
            return this.$country.isNot('ro')
          }
        }
      ],
      isAndroid: isAnyMobileDevice(),
      isPricesPage: false,
      environment: 'dev',
      branch: '',
      Retailer
    }
  },
  setup() {
    const authStore = useAuthStore()
    const dashboardStore = useDashboardStore()
    const notificationStore = useNotificationStore()
    const sideMenuStore = useSideMenuStore()
    const { user } = storeToRefs(authStore)
    const { menuItemsBadgeCount, lastOrders } = storeToRefs(dashboardStore)
    const { getNotificationsByKind } = storeToRefs(notificationStore)
    const { sidebarActive } = storeToRefs(sideMenuStore)

    return {
      user,
      menuItemsBadgeCount,
      lastOrders,
      getNotificationsByKind,
      sidebarActive,
      authStore,
      dashboardStore,
      sideMenuStore
    }
  },
  computed: {
    money() {
      return money
    },

    getSystemWideNotifications() {
      return this.getNotificationsByKind(NOTIFICATION_TYPES.BAR)
    },
    mobilePricePage() {
      return this.isPricesPage && this.isAndroid
    },
    isNotProduction() {
      return import.meta.env.MODE !== 'production'
    },
    isShowNotificationModal() {
      return this.$router.currentRoute.value.fullPath !== '/retailer/application/create'
    },
    listItemsWithBadgeCount() {
      return this.listItems.map(item => {
        return {
          ...item,
          badgeCount: this.menuItemsBadgeCount[item.name] ?? 0
        }
      })
    },
    currencySymbol() {
      return import.meta.env.VITE_CURRENCY_SYMBOL
    },
    notificationWrapperClass() {
      const classes = []

      const hasBreadcrumb = !this.mobilePricePage
      if (hasBreadcrumb) {
        classes.push('mt-3')
      } else {
        classes.push('mt-4')
      }

      const isOnDashboard = this.$router.currentRoute.value.name === 'retailer.dashboard'
      const hasVirtualPosAlert =
        this.$country.is('tr') && this.user?.retailer?.is_virtual_pos_application_active && isOnDashboard

      if (hasVirtualPosAlert) {
        classes.push('mb-0')
      } else {
        classes.push('mb-4')
      }

      return classes.join(' ')
    },
    isConsumerGoodsRetailer() {
      return Retailer.isConsumerGoods()
    },
    isAgricultureRetailer() {
      return Retailer.isAgriculture()
    }
  },

  created() {
    if (this.user) {
      const phone = this.user.phone ? this.user.phone.replace(/\(|\)| /g, '') : null
      const env = import.meta.env.MODE

      // InfosetChat should not be shown for consumer goods retailers
      if (env === 'production') {
        let apiKey = ''
        if (this.isAgricultureRetailer === true) {
          if (this.$country.is('tr')) {
            apiKey = '9sZcpeqiHefkPOSHkOoDAbkpo9MuWMb2UeiMedqh'
          }
          if (this.$country.is('ro')) {
            apiKey = 'fZybEuCmoOtnXvvTtzSXMPSmrREentYiBo98EF9V'
          }
        } else if (Retailer.isVehicleSales() === true) {
          apiKey = 'Bt5E4uRPjbGEkTOxKYi7xG4NEGTojNK7QBnqoftF'
        }

        if (apiKey)
          window.InfosetChat('boot', {
            widget: { apiKey: apiKey },
            visitor: {
              id: this.user.retailer.id,
              firstName: this.user.firstname,
              lastName: this.user.lastname,
              email: this.user.email,
              phone,
              customFields: {
                retailerName: this.user.retailer.name,
                retailerId: this.user.retailer.id,
                region: this.user.retailer.region_manager.region,
                country: this.$country.get()
              }
            }
          })
      }
    }

    if (this.$route.name === 'retailer.prices.index') {
      this.isPricesPage = true
    }
    const route = _.find(this.listItems, { name: this.$route.name })
    route?.routeEnter?.()
  },
  methods: {
    handleLogoClick() {
      const sideMenuStore = useSideMenuStore()
      sideMenuStore.setActiveMenuIndex('retailer.dashboard')
    },
    toggleBodyClass(method, bodyClass) {
      const el = document.body

      if (method === 'add') {
        el.classList.add(bodyClass)
      } else {
        el.classList.remove(bodyClass)
      }
    },
    enlargeElement() {
      document.body.className = 'enlarged'
    },
    determineEnvironment(hostname) {
      if (import.meta.env.MODE === 'production') {
        return
      }

      if (import.meta.env.MODE === 'development') {
        this.environment = 'dev'
        document.querySelector('#favicon').href = '/images/environment/favicon-develop.svg'
        return
      }

      if (hostname.includes('beykoz.tarfin.com') || hostname.includes('bucharest.tarfin.com')) {
        this.environment = 'staging'
        document.querySelector('#favicon').href = '/images/environment/favicon-staging.svg'
        return
      }

      this.environment = 'dynamic'
      document.querySelector('#favicon').href = '/images/environment/favicon-dynamic.svg'
    },
    extractDomainPrefix(hostname) {
      this.branch = hostname.split('.tarfin.com')[0]
    }
  },
  async beforeRouteEnter(to, from, next) {
    const sideMenuStore = useSideMenuStore()
    sideMenuStore.setActiveMenuIndex(to.name)

    const authStore = useAuthStore()
    const dashboardStore = useDashboardStore()

    await getUser(authStore)
    if (country.is('tr')) {
      await dashboardStore.getDashboardMenuItemsBadgeCount()
    }
    next()
  },
  async beforeRouteUpdate(to, from, next) {
    const authStore = useAuthStore()
    const dashboardStore = useDashboardStore()

    await getUser(authStore)
    if (country.is('tr') && sessionLockManager.lock('retailerDashboard.getDashboardMenuItemsBadgeCount', 1)) {
      await dashboardStore.getDashboardMenuItemsBadgeCount()
    }
    next()
  },
  mounted() {
    let leftBarCompactMode = null
    if (localStorage.getItem('sidebarOpened') === '0') {
      leftBarCompactMode = 'condensed'
    } else if (localStorage.getItem('sidebarOpened') === '1') {
      leftBarCompactMode = 'scrollable'
    } else {
      leftBarCompactMode = 'scrollable'
    }
    document.body.setAttribute('data-leftbar-compact-mode', leftBarCompactMode)
    document.body.setAttribute('data-leftbar-theme', 'light')

    // this.toggleBodyClass('add', 'enlarged')

    const hostname = window.location.hostname
    this.determineEnvironment(hostname)
    this.extractDomainPrefix(hostname)

    this.sideMenuStore.setSidebarCollapseState(false)

    // The panel is only reached once beforeRouteEnter has the user and the badge counts, so this
    // is the first moment there is something worth showing behind the loader a panel switch put
    // up. A no-op on every other visit, since nothing else raises it.
    usePageLoadingStore().stopFullPage()
  },
  unmounted() {
    document.body.removeAttribute('data-leftbar-compact-mode')
    document.body.removeAttribute('data-leftbar-theme')
    // this.toggleBodyClass('remove', 'enlarged')
  }
}
</script>

<style scoped>
@media (min-width: 1025px) {
  .test-environment-style {
    margin-top: 20px;
  }
}

.mx--1 {
  margin-left: -1rem !important;
  margin-right: -1rem !important;
}

.bg-marquee-item {
  background-color: #f4f5f9 !important;
}

.enlarge-icon {
  padding-left: 15%;
}

/*
 * The design puts the logo 22px from the sidebar's left edge and the switch 24px from its right.
 * justify-content lives here rather than on a Bootstrap utility class: the condensed rule below
 * has to override it, and the utility carries !important.
 */
.sidebar-header {
  justify-content: space-between;
  padding-left: 22px;
  padding-right: 24px;
}

/*
 * The condensed bar is seventy pixels wide and leaves the switch out, so the emblem goes back to
 * being centred on its own — the way it sat before the switch was put next to it.
 */
body[data-leftbar-compact-mode='condensed'] .sidebar-header {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
</style>
